import React from 'react';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/common/PageHeader';
import { SEO } from '@/components/seo';
import { useLanguage } from '@/contexts/LanguageContext';

const Terms = () => {
  const { isRTL } = useLanguage();

  return (
    <Layout>
      <SEO titleKey="seo.terms.title" descriptionKey="seo.terms.description" noindex={true} />
      <PageHeader
        title="Terms of Service"
        titleAr="شروط الخدمة"
        subtitle="Please read these terms carefully before using our platform"
        subtitleAr="يرجى قراءة هذه الشروط بعناية قبل استخدام منصتنا"
        size="sm"
      />

      <div className="container py-12 max-w-4xl" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="prose prose-emerald dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '1. قبول الشروط' : '1. Acceptance of Terms'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'باستخدام منصة هدى للمتقين، فإنك توافق على الالتزام بشروط الخدمة هذه. إذا لم توافق على أي من هذه الشروط، فلا يحق لك استخدام المنصة.'
                : 'By accessing and using the Hudan Lil Muttaqeen platform, you agree to be bound by these Terms of Service. If you do not agree to any of these terms, you are not permitted to use the platform.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '2. وصف الخدمة' : '2. Description of Service'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'هدى للمتقين هي منصة تعليمية عبر الإنترنت تربط الطلاب بمعلمين مؤهلين حاملين للإجازة لتعلم القرآن الكريم والتجويد والحفظ والدراسات الإسلامية.'
                : 'Hudan Lil Muttaqeen is an online educational platform that connects students with qualified, Ijazah-holding teachers for Quran recitation, Tajweed, Hifz (memorization), and Islamic studies.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '3. حسابات المستخدمين' : '3. User Accounts'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'يجب عليك تقديم معلومات دقيقة وكاملة عند إنشاء حساب. أنت مسؤول عن الحفاظ على سرية بيانات اعتماد حسابك وعن جميع الأنشطة التي تتم تحت حسابك.'
                : 'You must provide accurate and complete information when creating an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '4. التزامات المعلمين' : '4. Teacher Obligations'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'يجب على المعلمين تقديم بيانات اعتماد صحيحة بما في ذلك شهادات الإجازة. توفير جلسات تعليمية عالية الجودة والالتزام بالمواعيد المحددة.'
                : 'Teachers must provide valid credentials including Ijazah certificates. They are expected to deliver high-quality teaching sessions and adhere to scheduled session times.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '5. الدفع والاسترداد' : '5. Payment & Refunds'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'تتم معالجة المدفوعات بشكل آمن عبر بوابات الدفع المعتمدة. سياسة الإلغاء: أكثر من 24 ساعة = استرداد كامل، 12-24 ساعة = استرداد 50%، أقل من 12 ساعة = لا استرداد.'
                : 'Payments are processed securely through approved payment gateways. Cancellation policy: more than 24 hours = full refund, 12-24 hours = 50% refund, less than 12 hours = no refund.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '6. السلوك المقبول' : '6. Acceptable Conduct'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'يجب على جميع المستخدمين التعامل باحترام ومهنية. يحظر أي سلوك مسيء أو تمييزي أو غير لائق وقد يؤدي إلى تعليق أو إنهاء الحساب.'
                : 'All users must conduct themselves respectfully and professionally. Any abusive, discriminatory, or inappropriate behavior is prohibited and may result in account suspension or termination.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '7. الملكية الفكرية' : '7. Intellectual Property'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'جميع المحتويات والمواد التعليمية على المنصة محمية بموجب حقوق الملكية الفكرية. لا يجوز نسخ أو توزيع أي محتوى بدون إذن كتابي مسبق.'
                : 'All content and educational materials on the platform are protected by intellectual property rights. No content may be copied or distributed without prior written permission.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '8. تحديد المسؤولية' : '8. Limitation of Liability'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'المنصة ليست مسؤولة عن أي أضرار مباشرة أو غير مباشرة ناتجة عن استخدام الخدمة. نحن نسعى لتوفير خدمة موثوقة ولكن لا نضمن توفر الخدمة بشكل مستمر.'
                : 'The platform is not liable for any direct or indirect damages resulting from the use of the service. We strive to provide a reliable service but do not guarantee uninterrupted availability.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '9. التعديلات' : '9. Modifications'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'نحتفظ بالحق في تعديل هذه الشروط في أي وقت. سيتم إخطار المستخدمين بأي تغييرات جوهرية عبر البريد الإلكتروني أو إشعار على المنصة.'
                : 'We reserve the right to modify these terms at any time. Users will be notified of any material changes via email or platform notification.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">
              {isRTL ? '10. الاتصال بنا' : '10. Contact Us'}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {isRTL
                ? 'إذا كان لديك أي أسئلة حول شروط الخدمة هذه، يرجى التواصل معنا عبر البريد الإلكتروني: support@hudanlilmuttaqeen.com'
                : 'If you have any questions about these Terms of Service, please contact us at: support@hudanlilmuttaqeen.com'}
            </p>
          </section>

          <p className="text-sm text-muted-foreground pt-6 border-t">
            {isRTL ? 'آخر تحديث: مارس 2026' : 'Last updated: March 2026'}
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Terms;
